import { useState, useMemo, useCallback } from 'react'
import { useRecommendStylesAndFonts } from 'hooks/queries/brandKits/useRecommendStylesAndFonts'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { SuggestionRequestStatus } from 'types'

interface RecommendationResult {
  visual_style_ids?: number[]
  font_pairing_ids?: number[]
}

export const useStyleAndFontRecommendations = (brandKitId?: number) => {
  const [requestId, setRequestId] = useState<number | null>(null)

  const { mutateAsync: requestRecommendations, isLoading: isRequesting } = useRecommendStylesAndFonts()

  const { data } = usePollAISuggestionRequest({ id: requestId || 0 }, { enabled: !!requestId })

  const isSuccess = data?.request?.status === SuggestionRequestStatus.Successful
  const isFailed = data?.request?.status === SuggestionRequestStatus.Failed

  const result = useMemo((): RecommendationResult | undefined => {
    if (!isSuccess || !data?.request?.generated_content) return undefined
    try {
      return JSON.parse(data.request.generated_content) as RecommendationResult
    } catch {
      return undefined
    }
  }, [isSuccess, data?.request?.generated_content])

  const fetch = useCallback(async () => {
    if (!brandKitId || requestId || isRequesting) return
    try {
      const response = await requestRecommendations({ brandKitId })
      setRequestId(response.ai_suggestion_request_id)
    } catch {
      // Silently fail - falls back to default styles
    }
  }, [brandKitId, requestId, isRequesting, requestRecommendations])

  const isLoading = isRequesting || (!!requestId && !isSuccess && !isFailed)

  return {
    visualStyleIds: result?.visual_style_ids,
    fontPairingIds: result?.font_pairing_ids,
    isLoading,
    fetch,
  }
}
